import {
  Dimensions,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { Text } from '@/components/Text';
import { useStoreActions, useStoreState } from 'easy-peasy';
import CustomCheckBox from '@/components/SharedComponents/CustomCheckBox/CustomCheckBox';
import React, { useContext, useEffect, useState } from 'react';
import { fetchUserConsents, updateUserEpa } from '@/api/services/user';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { navigate } from '@/navigations/RootNavigation';
import colors, { appTheme } from '@/config/colors';
import moment from 'moment';
import { connectWithEpa, writeToEpa } from '@/utils/epaAuth';
import { EpaAuthContext } from '@/containers/EpaAuthContextProvider';

export default function EpaScreen() {
  const storedUser = useStoreState((state) => state.signedInUser);
  const userConsents = useStoreState((state) => state.userConsents);
  const setUserInsuranceId = useStoreActions(
    (actions) => actions.setUserInsuranceId,
  );
  const toggleUserAutoUpload = useStoreActions(
    (actions) => actions.setUserIsAutoUploadEpa,
  );
  let [isConnectEnabled, setConnectState] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState(
    Dimensions.get('window'),
  );
  let [showEpaSuccess, setShowEpaSuccess] = useState(false);
  let [showEpaError, setShowEpaError] = useState(false);
  let [showEpaConnectError, setShowEpaConnectError] = useState(false);

  const authContext = useContext(EpaAuthContext);
  const epaLastUpdatedAt = moment(
    storedUser.user?.userEpa?.epaLastUpdatedAt,
  ).format('DD-MM-YYYY HH:mm');
  const consentText =
    'Ich willige ein, dass Übungsdauer und Übungsergebnisse von neolexon in meiner persönlichen elektronischen Patientenakte gespeichert werden. Ich kann meine Einwilligung jederzeit widerrufen. Die Einwilligung ist optional.';

  const styles = StyleSheet.create({
    fullHeight: {
      flex: 1,
      flexDirection: 'column',
    },
    container: {
      flex: 1,
      flexDirection: 'column',
      paddingTop: 10,
      width: windowDimensions.width > 1024 ? '70%' : '100%',
      paddingHorizontal: 10,
      marginHorizontal: 'auto',
    },
    statusText: {
      fontSize: 18,
      paddingTop: 10,
      textAlign: 'center',
    },
    heading: {
      paddingTop: 25,
      fontSize: 20,
      paddingBottom: 20,
      textAlign: 'center',
    },
    consentContainer: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      paddingBottom: 10,
    },
    textMargin: {
      marginTop: 10,
    },
    text: {
      fontSize: 16,
    },
    textSmall: {
      fontSize: 14,
    },
    textCenter: {
      textAlign: 'center',
    },
    textGreen: {
      color: colors.green,
    },
    textRed: {
      color: colors.red,
    },
    epaContainer: {
      marginTop: 20,
      marginBottom: 20,
      alignSelf: 'center',
    },
    itemsCenter: {
      alignItems: 'center',
    },
    linkToConsents: {
      fontWeight: 'bold',
    },
    button: {
      backgroundColor: appTheme.primary,
    },
    buttonStyle: {
      backgroundColor: appTheme.primary,
      borderRadius: 8,
      padding: 15,
      marginVertical: 10,
    },
    buttonTextStyle: {
      color: colors.white,
      textAlign: 'center',
      fontWeight: '700',
    },
  });

  /**
   * @param {boolean} isAutoUpload
   */
  async function updateEpaSync(isAutoUpload) {
    await updateUserEpa({ autoUpload: isAutoUpload });
  }

  useEffect(() => {
    const dimensionChange = Dimensions.addEventListener(
      'change',
      ({ window }) => {
        setWindowDimensions(window);
      },
    );
    return () => dimensionChange?.remove();
  }, []);

  useEffect(() => {
    const consents = async () => {
      await fetchUserConsents();
    };
    consents();
  }, []);

  let canWriteToEpa = false;
  for (let consent of userConsents) {
    if (consent.name === 'WriteToEpa') {
      canWriteToEpa = consent.givenConsent;
    }
  }

  return (
    <SafeAreaView style={styles.fullHeight}>
      <ScrollView>
        <View style={styles.container}>
          <Text style={[styles.text, styles.textMargin, styles.textCenter]}>
            Alle gesetzlich Versicherten können eine elektronische Patientenakte
            (ePA) von ihrer Krankenkasse erhalten, in der medizinische Befunde
            und weitere persönliche Gesundheitsdaten gespeichert werden. Sie
            können selbst bestimmen, ob und in welchem Umfang Sie die ePA nutzen
            möchten.
          </Text>
          <Text style={[styles.text, styles.textMargin, styles.textCenter]}>
            Sie haben hierbei auch die Möglichkeit die Übungsdauer und
            Übungsergebnisse aus neolexon in ihre ePA zu übertragen. Bei der
            automatischen Übertragung werden vergangene Übungsdaten jeden Tag um
            22:00 Uhr in die ePA übertragen.
          </Text>
          {storedUser.user?.idpInsuranceId && !canWriteToEpa && (
            <Text
              style={[
                styles.text,
                styles.textCenter,
                styles.textMargin,
                styles.textRed,
              ]}
            >
              Es liegt keine Einwilligung für die Speicherung der Übungen in
              ihre ePA vor. Sie können ihre Einwilligungen{' '}
              <PressableNeolexon
                onPress={() => {
                  navigate('Einwilligungen');
                }}
                style={[styles.linkToConsents]}
              >
                <Text>hier</Text>
              </PressableNeolexon>{' '}
              verwalten.
            </Text>
          )}
          {storedUser.user?.idpInsuranceId && canWriteToEpa && (
            <View style={[styles.textMargin, styles.itemsCenter]}>
              <Text style={styles.statusText}>
                Status: GesundheitsID verbunden
              </Text>
              <View style={styles.statusText}>
                <CustomCheckBox
                  checked={storedUser.user?.userEpa?.autoUpload ?? false}
                  onChecked={() => {
                    toggleUserAutoUpload(!storedUser.user?.userEpa?.autoUpload);
                    updateEpaSync(!storedUser.user?.userEpa?.autoUpload);
                  }}
                  disabled={!canWriteToEpa}
                  checkedColor={appTheme.primary}
                  label={'Ergebnisse automatisch in die ePA schreiben'}
                />
              </View>
              <PressableNeolexon
                style={styles.buttonStyle}
                onPress={async () => {
                  let writeToEpaSuccess = false;
                  try {
                    await writeToEpa();
                    writeToEpaSuccess = true;
                  } catch {}
                  setShowEpaError(!writeToEpaSuccess);
                  setShowEpaSuccess(writeToEpaSuccess);
                }}
                disabled={!canWriteToEpa}
              >
                <Text style={styles.buttonTextStyle}>
                  {'Ergebnisse in meine ePA übertragen'}
                </Text>
              </PressableNeolexon>
              <Text>Letztes ePA Update: {epaLastUpdatedAt}</Text>

              {showEpaSuccess ? (
                <Text
                  style={[
                    styles.textSmall,
                    styles.textCenter,
                    styles.textGreen,
                  ]}
                >
                  Übungsergebnisse erfolgreich in die ePA geschrieben
                </Text>
              ) : null}

              {showEpaError && (
                <Text
                  style={[styles.textSmall, styles.textCenter, styles.textRed]}
                >
                  Es ist ein Fehler beim Schreiben in die ePA aufgetreten. Bitte
                  probieren Sie es später erneut.
                </Text>
              )}
              <Text
                style={[styles.textSmall, styles.textCenter, styles.textMargin]}
              >
                Hinweis: Sie können sich nun auch mit Ihrer GesundheitsID in der
                neolexon App einloggen.
              </Text>
            </View>
          )}
          {!storedUser.user?.idpInsuranceId && (
            <View>
              <View style={styles.consentContainer}>
                <CustomCheckBox
                  checked={isConnectEnabled}
                  onChecked={(value) => {
                    setConnectState(!value);
                  }}
                />

                <Text style={[styles.text, styles.textMargin]}>
                  {consentText}
                </Text>
              </View>
              <PressableNeolexon
                style={styles.buttonStyle}
                onPress={async () => {
                  await connectWithEpa(
                    authContext,
                    (insuranceId) => {
                      setUserInsuranceId(insuranceId);
                      fetchUserConsents();
                    },
                    () => {
                      setShowEpaConnectError(true);
                    },
                  );
                }}
                disabled={!isConnectEnabled}
              >
                <Text style={styles.buttonTextStyle}>{'Verbinden'}</Text>
              </PressableNeolexon>
              {showEpaConnectError && (
                <Text
                  style={[styles.textSmall, styles.textCenter, styles.textRed]}
                >
                  Es ist ein Fehler beim Verbinden mit der ePA aufgetreten.
                  Falls dieses Problem besteht, melden Sie sich bitte bei
                  unserem Support.
                </Text>
              )}
              <Text
                style={[styles.textSmall, styles.textCenter, styles.textMargin]}
              >
                Hinweis: hierzu müssen Sie sich einmalig mit Ihrer GesundheitsID
                anmelden, um sich mit der ePA zu verbinden
              </Text>
            </View>
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
